import { AbstractFormatter } from '../Formatter';
import { TypedValue } from '../DataPrimitive';
import { DataSeries } from '../DataSeries';
import { DataPoint } from '../DataPoint';
import { DataFrame } from '../DataFrame';
import EncodingExecutor from '../EncodingExecutor';

export interface LerpConfig {
    outputMin?: number;
    outputMax?: number;
    inputMin?: number;
    inputMax?: number;
}

/**
 * Linearly maps numeric values onto a new numeric range.
 *
 * Similar to the `gradient` formatter, the input domain is derived from the data (per-series or
 * DataFrame-wide) and the output is interpolated between the configured output range.
 *
 * `outputMin` and `outputMax` default to `[0, 1]` when no config is provided.
 * `inputMin` and `inputMax` are derived from the input data unless provided.
 *
 * ```js
 * <SampleViz
 *     context={{
 *         nodeSizeConfig: {
 *             inputMin: 0,
 *             inputMax: 100,
 *             outputMin: 10,
 *             outputMax: 60,
 *         },
 *     }}
 *     options={{
 *         nodeValues: '> primary | seriesByName("nodeValues")',
 *         nodeSize: '> nodeValues | lerp(nodeSizeConfig)',
 *     }}
 *     dataSources={{
 *         primary: {
 *             data: {
 *                 fields: [{ name: 'nodeValues' }],
 *                 columns: [[0, 25, 50, 75, 100]],
 *             },
 *         },
 *     }}
 * />
 * ```
 *
 * ## Config Object
 *  * **outputMin**: `number` minimum value of the mapped output range. Defaults to `0`.
 *  * **outputMax**: `number` maximum value of the mapped output range. Defaults to `1`.
 *  * **inputMin**: `number` lower bound of the input domain. Optional.
 *  * **inputMax**: `number` upper bound of the input domain. Optional.
 *
 * If the input value is outside of `[inputMin, inputMax]`, the value is clamped to the nearest bound.
 *
 * @extends AbstractFormatter<'number', 'number'>
 */
export class Lerp extends AbstractFormatter<'number', 'number'> {
    readonly config: LerpConfig;

    readonly outputMin: number;

    readonly outputMax: number;

    readonly inputMin?: number;

    readonly inputMax?: number;

    constructor(config?: LerpConfig) {
        super();
        this.config = EncodingExecutor.rawTree(config) || {};

        this.outputMin = this.config.outputMin ?? 0;
        this.outputMax = this.config.outputMax ?? 1;
        this.inputMin = this.config.inputMin;
        this.inputMax = this.config.inputMax;

        if (
            !Lerp.isValidNumber(this.inputMin) ||
            !Lerp.isValidNumber(this.inputMax) ||
            !Lerp.isValidNumber(this.outputMin) ||
            !Lerp.isValidNumber(this.outputMax)
        ) {
            throw new Error(
                `Lerp config: inputMin, inputMax, outputMin, and outputMax should be valid finite numbers. Received: inputMin=${this.inputMin}, inputMax=${this.inputMax}, outputMin=${this.outputMin}, outputMax=${this.outputMax}`
            );
        }

        // throw error if outputMin > outputMax
        if (this.outputMin !== undefined && this.outputMax !== undefined && this.outputMin > this.outputMax) {
            throw new Error(
                `Lerp config: outputMin should not be greater than outputMax. Received: outputMin=${this.outputMin}, outputMax=${this.outputMax}`
            );
        }
        // throw error if inputMin > inputMax
        if (this.inputMin !== undefined && this.inputMax !== undefined && this.inputMin > this.inputMax) {
            throw new Error(
                `Lerp config: inputMin should not be greater than inputMax. Received: inputMin=${this.inputMin}, inputMax=${this.inputMax}`
            );
        }
    }

    private interpolate(value: number, inMin: number, inMax: number): number | undefined {
        if (!Number.isFinite(value) || !Number.isFinite(inMin) || !Number.isFinite(inMax)) {
            return undefined;
        }
        // handle uniform series by returning midpoint of output range, this is what gradient does.
        if (inMax === inMin) {
            return this.outputMin + 0.5 * (this.outputMax - this.outputMin);
        }
        const t = (value - inMin) / (inMax - inMin);
        // clamp values if the value is outside the specified inputMin and inputMax.
        const clamped = Math.min(1, Math.max(0, t));
        return this.outputMin + clamped * (this.outputMax - this.outputMin);
    }

    private static isValidNumber(value?: number): boolean {
        return value === undefined || Number.isFinite(value);
    }

    private resolveSeriesMinMax(series?: DataSeries, fallback?: number): { inMin: number; inMax: number } {
        let inMin = Number.isFinite(this.inputMin) ? Number(this.inputMin) : Number(fallback);
        let inMax = Number.isFinite(this.inputMax) ? Number(this.inputMax) : Number(fallback);

        if (series?.points?.length && (!Number.isFinite(this.inputMin) || !Number.isFinite(this.inputMax))) {
            const seriesMin = Number(series.min()?.getRawValue());
            const seriesMax = Number(series.max()?.getRawValue());

            if (!Number.isFinite(this.inputMin) && Number.isFinite(seriesMin)) {
                inMin = seriesMin;
            }
            if (!Number.isFinite(this.inputMax) && Number.isFinite(seriesMax)) {
                inMax = seriesMax;
            }
        }

        return { inMin, inMax };
    }

    private resolveFrameMinMax(dataFrame: DataFrame): { inMin: number; inMax: number } {
        const hasConfiguredMin = Number.isFinite(this.inputMin);
        const hasConfiguredMax = Number.isFinite(this.inputMax);
        let globalMin = hasConfiguredMin ? Number(this.inputMin) : Number.POSITIVE_INFINITY;
        let globalMax = hasConfiguredMax ? Number(this.inputMax) : Number.NEGATIVE_INFINITY;

        dataFrame.series.forEach((series: DataSeries) => {
            if (series?.points?.length && (!hasConfiguredMin || !hasConfiguredMax)) {
                const sMin = Number(series.min()?.getRawValue());
                const sMax = Number(series.max()?.getRawValue());
                if (!hasConfiguredMin && !Number.isNaN(sMin)) {
                    globalMin = Math.min(globalMin, sMin);
                }
                if (!hasConfiguredMax && !Number.isNaN(sMax)) {
                    globalMax = Math.max(globalMax, sMax);
                }
            }
        });

        const inMin = hasConfiguredMin ? Number(this.inputMin) : globalMin;
        const inMax = hasConfiguredMax ? Number(this.inputMax) : globalMax;
        return { inMin, inMax };
    }

    // Override to support DataFrame-wide min/max when lerp is applied to a table (DataFrame)
    format(dataPrimitive: any): any {
        if (dataPrimitive instanceof DataFrame) {
            const { inMin, inMax } = this.resolveFrameMinMax(dataPrimitive);
            if (!Number.isFinite(inMin) || !Number.isFinite(inMax)) {
                return super.format(dataPrimitive);
            }

            const mappedSeries: DataSeries<'number'>[] = dataPrimitive.series.map((series: DataSeries) => {
                const outPoints = series.points.map(p => {
                    const input = p.getValue();
                    if (input.type !== 'number') {
                        return new DataPoint(p.field, { type: 'number', value: undefined }, p.isSearchResult);
                    }
                    const mapped = this.interpolate(input.value as unknown as number, inMin, inMax);
                    return new DataPoint(p.field, { type: 'number', value: mapped }, p.isSearchResult);
                });
                return new DataSeries<'number'>(outPoints);
            });

            return new DataFrame<'number'>(mappedSeries);
        }
        return super.format(dataPrimitive);
    }

    protected formatTypedValue(p: DataPoint<'number'>, series?: DataSeries): TypedValue<'number'> {
        const input: TypedValue<'number'> = p.getValue();
        const { type, value } = input;
        if (type !== 'number') {
            return { type: 'number', value: undefined };
        }

        const { inMin, inMax } = this.resolveSeriesMinMax(series, value as unknown as number);
        const mapped = this.interpolate(value as unknown as number, inMin, inMax);
        return { type: 'number', value: mapped };
    }
}
